import {
  BackofficeReorderProductHighlightBody,
  BackofficeReorderProductHighlightParams,
  BackofficeReorderProductHighlightResponse,
} from '@diamond/shared/types';
import { apiInstance } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const BACKOFFICE_GROUPED_PROMOS_PRODUCT_HIGHLIGHT_KEY =
  'BackofficeGroupedPromosProductHighlight';

/**
 * Sends a request to reorder a product highlight within a promotion.
 *
 * @param token - The authentication token for the API request.
 * @param params - The parameters identifying the promotion (e.g., promo ID).
 * @param body - The request body containing the new index and product item code.
 * @returns A promise that resolves to the API response.
 * @throws Throws an error if the API request fails.
 */
export const reorderProductHighlight = async (
  token: string,
  params: BackofficeReorderProductHighlightParams,
  body: BackofficeReorderProductHighlightBody
): Promise<BackofficeReorderProductHighlightResponse> => {
  const { id } = params;
  const apiUrl = `backoffice/promo/${id}/reorder-product`;

  try {
    const { data: result } = await apiInstance(
      token
    ).patch<BackofficeReorderProductHighlightResponse>(apiUrl, body);
    return result;
  } catch (error) {
    console.error('Error reordering product highlight via apiInstance:', error);
    throw error;
  }
};

type ReorderProductHighlightMutationArgs = {
  id: string;
  index: number;
  product_item_code: number;
};

export const BACKOFFICE_REORDER_PRODUCT_HIGHLIGHT_MUTATION_KEY =
  'BackofficeReorderProductHighlight';

/**
 * React Query hook for managing the state of the product highlight reorder operation.
 *
 * @returns An object containing the mutation state and the mutate function.
 */
export function useReorderProductHighlightMutation() {
  const queryClient = useQueryClient();
  const { access_token } = useAuthStore();

  return useMutation<
    BackofficeReorderProductHighlightResponse,
    Error,
    ReorderProductHighlightMutationArgs
  >({
    mutationFn: ({
      id,
      index,
      product_item_code,
    }: ReorderProductHighlightMutationArgs) => {
      const params: BackofficeReorderProductHighlightParams = { id };
      const body: BackofficeReorderProductHighlightBody = {
        index,
        product_item_code,
      };
      return reorderProductHighlight(access_token, params, body);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          BACKOFFICE_GROUPED_PROMOS_PRODUCT_HIGHLIGHT_KEY,
          variables.id,
        ],
      });
    },
  });
}
